import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { SearchIcon } from 'lucide-react';
import { Container, Section, Kicker, Pill } from '../components/ui/Primitives';
import { ARTICLES } from '../data/insights';
import { SERVICE_LINES, SECTORS } from '../data/services';
const PAGE_SIZE = 4;
export function Insights() {
  // The site declares a SearchAction on /insights?q=, so the parameter has to
  // actually seed the search rather than only exist in the schema.
  const [searchParams] = useSearchParams();
  const [query, setQuery] = React.useState(searchParams.get('q') ?? '');
  const [line, setLine] = React.useState<string>('all');
  const [sector, setSector] = React.useState<string>('all');
  const [visible, setVisible] = React.useState(PAGE_SIZE);
  const featured = ARTICLES.find(article => article.featured) ?? ARTICLES[0];
  const filtered = React.useMemo(() => {
    const term = query.trim().toLowerCase();
    return ARTICLES.filter(article => {
      if (line !== 'all' && article.serviceLine !== line) return false;
      if (sector !== 'all' && !article.sectors.includes(sector)) return false;
      if (!term) return true;
      return article.title.toLowerCase().includes(term) || article.excerpt.toLowerCase().includes(term) || article.sectors.some(entry => entry.toLowerCase().includes(term));
    });
  }, [query, line, sector]);
  React.useEffect(() => setVisible(PAGE_SIZE), [query, line, sector]);
  const shown = filtered.slice(0, visible);
  return <>
      <section className="surface-dark" data-id="element-1048" data-mp="16zyvctq7z887">
        <Container className="py-14 sm:py-20" data-id="element-1049" data-mp="1krvi0i94cpx7">
          <Kicker data-id="element-1050" data-mp="1kqk6fw9fmdvj">Insights</Kicker>
          <h1 className="mt-4 max-w-3xl font-display text-[34px] leading-tight text-white sm:text-[46px]" data-id="element-1051" data-mp="1ktpur9a8lbq7">
            Analysis for people who have to act on it
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] text-white/72" data-id="element-1052" data-mp="1uqkutwm5rk6l">
            We publish what we would tell a client. Specific, sourced and short enough to read before a meeting.
          </p>
        </Container>
      </section>

      <Section aria-labelledby="featured" data-id="element-1053" data-mp="1wsbrss789rw1">
        <h2 id="featured" className="sr-only" data-id="element-1054" data-mp="21uatzyr8eire">
          Featured article
        </h2>
        <article className="grid items-center gap-8 rounded-md border border-line p-5 sm:p-8 lg:grid-cols-2" data-id="element-1055" data-mp="26s2jyn8bfusy">
          <Link to={`/insights/${featured.slug}`} className="block overflow-hidden rounded-sm" data-id="element-1056" data-mp="2bk43mu7tcnt0">
            <img src={featured.heroImage} alt={featured.heroAlt} className="aspect-[3/2] w-full object-cover" data-id="element-1057" data-mp="2gh3z6b71e85v" />
          </Link>
          <div data-id="element-1058" data-mp="2piby8vhdnd10">
            <Kicker data-id="element-1059" data-mp="2pjvcyhndxg0o">Featured</Kicker>
            <h3 className="mt-4 font-display text-[26px] leading-tight text-ink sm:text-[32px]" data-id="element-1060" data-mp="2pgr1r0bi5k0i">
              <Link to={`/insights/${featured.slug}`} className="underline-offset-4 hover:underline" data-id="element-1061" data-mp="2uh590g5a4pxh">
                {featured.title}
              </Link>
            </h3>
            <p className="mt-4 text-[16px] text-body" data-id="element-1062" data-mp="104ovqve2gbj4">{featured.excerpt}</p>
            <p className="mt-5 text-small text-muted" data-id="element-1063" data-mp="skwcm8q2cur4">
              {featured.author.name}, {featured.author.role} · {featured.readTime} minute read
            </p>
          </div>
        </article>
      </Section>

      <Section tone="mist" aria-labelledby="all-insights" data-id="element-1064" data-mp="ivmmgyes5m5n">
        <h2 id="all-insights" className="font-display text-[28px] text-ink" data-id="element-1065" data-mp="dvi7jctiszhj">
          All insights
        </h2>

        <div className="mt-8 grid gap-4 rounded-md border border-line bg-white p-5 lg:grid-cols-[1.4fr_1fr_1fr]" data-id="element-1066" data-mp="1bjs9jrtb3n8n">
          <div data-id="element-1067" data-mp="16jw5jsklzvt3">
            <label htmlFor="insight-search" className="block text-[13px] font-medium text-ink" data-id="element-1068" data-mp="16lgy5cnd54ob">
              Search
            </label>
            <div className="relative mt-2" data-id="element-1069" data-mp="11mvxjc478izc">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-1070" data-mp="wmrizrmupybr" />
              <input id="insight-search" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search titles and sectors" className="w-full rounded-sm border border-line py-2.5 pl-9 pr-3 text-[15px] text-ink" data-id="element-1071" data-mp="3hbp7p9cq8elk" />
            </div>
          </div>
          <div data-id="element-1072" data-mp="151npk4x3t0ag">
            <label htmlFor="insight-line" className="block text-[13px] font-medium text-ink" data-id="element-1073" data-mp="1514acvyu8q04">
              Service line
            </label>
            <select id="insight-line" value={line} onChange={event => setLine(event.target.value)} className="mt-2 w-full rounded-sm border border-line px-3 py-2.5 text-[15px] text-ink" data-id="element-1074" data-mp="1ss9lixtu2u43">
              <option value="all" data-id="element-1075" data-mp="1nrly0m4tcvem">All lines</option>
              {SERVICE_LINES.map(item => <option key={item.id} value={item.id} data-id="element-1076" data-mp="7dvqt6iedr0r">
                  {item.name}
                </option>)}
            </select>
          </div>
          <div data-id="element-1077" data-mp="cavq8ykqrviw">
            <label htmlFor="insight-sector" className="block text-[13px] font-medium text-ink" data-id="element-1078" data-mp="2qkr6eq5a99ik">
              Sector
            </label>
            <select id="insight-sector" value={sector} onChange={event => setSector(event.target.value)} className="mt-2 w-full rounded-sm border border-line px-3 py-2.5 text-[15px] text-ink" data-id="element-1079" data-mp="2ll4tyixa3xat">
              <option value="all" data-id="element-1080" data-mp="27u00jyki818x">All sectors</option>
              {SECTORS.map(item => <option key={item} value={item} data-id="element-1081" data-mp="22vorhq219urr">
                  {item}
                </option>)}
            </select>
          </div>
        </div>

        <p aria-live="polite" className="mt-6 text-small text-muted" data-id="element-1082" data-mp="2goxw7df1ds3y">
          {filtered.length} {filtered.length === 1 ? 'article' : 'articles'}
        </p>

        {filtered.length === 0 ? <div className="mt-6 rounded-md border border-dashed border-line bg-white p-10 text-center" data-id="element-1083" data-mp="1o4piidxad47n">
            <p className="font-display text-[20px] text-ink" data-id="element-1084" data-mp="1k2ujxh6qp4xk">Nothing matches those filters</p>
            <p className="mt-3 text-[15px] text-body" data-id="element-1085" data-mp="3j9f96n8o23rs">
              Try a broader search, or clear the sector filter to see the full library.
            </p>
          </div> : <ul className="mt-6 space-y-6" data-id="element-1086" data-mp="3t00baknjtqfg">
            {shown.map(article => <li key={article.slug} data-id="element-1087" data-mp="1rvzoy4t1lau">
                <article className="grid gap-6 rounded-md border border-line bg-white p-5 sm:grid-cols-[240px_1fr] sm:p-6" data-id="element-1088" data-mp="1v1pkfl9cr7m">
                  <Link to={`/insights/${article.slug}`} className="block overflow-hidden rounded-sm" data-id="element-1089" data-mp="359o0n20x5fl6">
                    <img src={article.heroImage} alt={article.heroAlt} loading="lazy" className="aspect-[3/2] w-full object-cover" data-id="element-1090" data-mp="3a8hdqtcan4kt" />
                  </Link>
                  <div data-id="element-1091" data-mp="pkw3z1l86ntz">
                    <div className="flex flex-wrap gap-2" data-id="element-1092" data-mp="uhmhbh5nb5bp">
                      <Pill data-id="element-1093" data-mp="ug1mrrjbzmdu">{SERVICE_LINES.find(item => item.id === article.serviceLine)?.short}</Pill>
                      {article.sectors.map(entry => <Pill key={entry} data-id="element-1094" data-mp="2a1vfgja5mlza">{entry}</Pill>)}
                    </div>
                    <h3 className="mt-4 font-display text-[22px] leading-snug text-ink" data-id="element-1095" data-mp="2592d2225rpqd">
                      <Link to={`/insights/${article.slug}`} className="underline-offset-4 hover:underline" data-id="element-1096" data-mp="20bso0niolg32">
                        {article.title}
                      </Link>
                    </h3>
                    <p className="mt-3 text-[15px] text-body" data-id="element-1097" data-mp="1r99nwb6a5hki">{article.excerpt}</p>
                    <p className="mt-4 text-small text-muted" data-id="element-1098" data-mp="1maq1yaros5s5">
                      {new Date(article.publishedAt).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}{' '}
                      · {article.readTime} minute read
                    </p>
                  </div>
                </article>
              </li>)}
          </ul>}

        {visible < filtered.length && <div className="mt-8 text-center" data-id="element-1099" data-mp="2pivddcccx3ho">
            <button type="button" onClick={() => setVisible(current => current + PAGE_SIZE)} className="rounded-sm border border-royal/30 px-6 py-3 text-[15px] text-royal transition-colors hover:bg-mist" data-id="element-1100" data-mp="2plr9qw8k3k40">
              Load more articles
            </button>
          </div>}
      </Section>
    </>;
}